/**
 * Register this device for push, and tell the server where to find it.
 *
 * This is the middle of a chain that was built at both ends and joined at
 * neither. The server has had a valid FCM service account, a `push_tokens`
 * table, `POST /users/me/push-token` to fill it, and delivery with stale-token
 * cleanup — all of it working, all of it waiting on a token that nothing ever
 * asked Firebase for. `/health` reported `push: true` throughout, because it
 * checks that the key parses, not that a phone could receive anything. So a
 * closed app could not be reached at all, and nothing said so.
 *
 * No `@capacitor/*` import, deliberately. `main.tsx` states the rule: the same
 * build serves the browser PWA and both native webviews, and it must keep
 * working as a plain web app. Capacitor injects its plugins on the window
 * inside the native shell only, so they are read defensively here and every
 * path degrades to a no-op in a browser rather than throwing.
 *
 * Nothing here is allowed to break sign-in. A mother who cannot log in because
 * a notification permission prompt failed is a far worse outcome than a mother
 * who does not get notifications, so every failure is swallowed after logging.
 */
import { api } from '../api/client';

type PermissionResult = { receive: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' };

interface PushChannel {
  id: string;
  name: string;
  description?: string;
  /** 1 = min, 3 = default, 5 = max. Only 4+ makes a sound or heads-up. */
  importance: 1 | 2 | 3 | 4 | 5;
  /** -1 secret, 0 private (content hidden on a locked screen), 1 public. */
  visibility?: -1 | 0 | 1;
  sound?: string;
  vibration?: boolean;
}

/** What Capacitor hands us for a delivered notification. */
export interface PushArrival {
  title?: string;
  body?: string;
  data?: Record<string, string | undefined>;
}

interface PushPlugin {
  checkPermissions(): Promise<PermissionResult>;
  requestPermissions(): Promise<PermissionResult>;
  register(): Promise<void>;
  createChannel?(channel: PushChannel): Promise<void>;
  addListener(
    event: 'registration' | 'registrationError' | 'pushNotificationReceived' | 'pushNotificationActionPerformed',
    handler: (payload: never) => void,
  ): Promise<{ remove: () => Promise<void> }>;
  removeAllListeners(): Promise<void>;
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  platform?: string;
  Plugins?: { PushNotifications?: PushPlugin };
}

function capacitor(): CapacitorGlobal | undefined {
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

/** Same defensive test main.tsx uses, for the same reason. */
function isNative(): boolean {
  const c = capacitor();
  if (!c) return false;
  return c.isNativePlatform?.() ?? ((c.getPlatform?.() ?? c.platform ?? 'web') !== 'web');
}

function platformName(): 'ios' | 'android' | 'web' {
  const p = capacitor()?.getPlatform?.() ?? capacitor()?.platform ?? 'web';
  return p === 'ios' || p === 'android' ? p : 'web';
}

/** The token this device most recently registered, so logout can withdraw it. */
let currentToken: string | null = null;

/**
 * What the app does when a notification arrives, and when one is tapped.
 *
 * Kept as settable callbacks rather than wired in here, because both answers
 * need the router and the query cache, and this module deliberately knows about
 * neither. App.tsx supplies them from inside the Router.
 */
export interface PushHandlers {
  /** A push arrived while the app was open and in front of her. */
  onForeground?: (n: PushArrival) => void;
  /** She tapped a notification, which opened or resumed the app. */
  onOpened?: (n: PushArrival) => void;
}
let handlers: PushHandlers = {};
export function setPushHandlers(h: PushHandlers): void { handlers = h; }

/* Listeners are attached once, at startup, and never torn down.

   They were previously attached inside registerForPush, which only runs once
   she is signed in — so a notification tapped on a cold start could arrive
   before anything was listening, and be lost. There is no queue: Capacitor
   emits the event when it emits it. Attaching at launch is the only way to be
   there for it. */
let listenersAttached = false;

async function attachListeners(push: PushPlugin): Promise<void> {
  if (listenersAttached) return;
  listenersAttached = true;

  await push.addListener('registration', ((t: { value: string }) => {
    currentToken = t.value;
    void api.post('/users/me/push-token', { token: t.value, platform: platformName() })
      .catch((err) => console.warn('[push] could not hand the token to the server', err));
  }) as (p: never) => void);

  await push.addListener('registrationError', ((err: unknown) => {
    /* On iOS the commonest cause is a missing APNs key, which no amount of
       retrying fixes — so this is reported, not retried. */
    console.warn('[push] the platform refused to issue a token', err);
  }) as (p: never) => void);

  await push.addListener('pushNotificationReceived', ((n: PushArrival) => {
    handlers.onForeground?.(n);
  }) as (p: never) => void);

  await push.addListener('pushNotificationActionPerformed', ((a: { notification?: PushArrival }) => {
    handlers.onOpened?.(a?.notification ?? {});
  }) as (p: never) => void);
}

/**
 * Two Android channels: one that interrupts, one that waits.
 *
 * On Android 8+ the channel decides whether a notification makes a sound or
 * appears over what she is doing. FCM priority does not — a "high priority"
 * message in a default-importance channel arrives silently. With no channel of
 * our own, everything fell into Android's fcm_fallback_notification_channel at
 * importance 3, so the crisis alert and the weekly nudge were equally quiet.
 *
 * Both ask for visibility 0 (private), so a locked screen shows the app name
 * rather than the text. Ask is the right word: Android reported the channel
 * back as VISIBILITY_NO_OVERRIDE (-1000) on a Samsung handset, because
 * lock-screen visibility is finally the user's global setting and an app's
 * preference is advisory. **So this cannot be relied on.** The protection that
 * actually holds is the copy itself: notification text must be discreet enough
 * to be read over her shoulder, the same discipline the SMS copy follows.
 * Stigma is high in these markets. The request is kept as defence in depth,
 * not as the defence.
 *
 * Channel settings are fixed at creation: Android ignores changes to an
 * existing channel, and only the user can alter it afterwards. Changing the
 * importance later needs a new id.
 */
/**
 * Everything push needs in place before anyone signs in: the channels that
 * describe how this app may interrupt, and the listeners that must already be
 * there when a tapped notification opens the app cold.
 */
export async function initPush(): Promise<void> {
  const push = capacitor()?.Plugins?.PushNotifications;
  if (!isNative() || !push) return;
  await ensureAndroidChannels();
  try {
    await attachListeners(push);
  } catch (err) {
    console.warn('[push] could not attach listeners', err);
  }
}

export async function ensureAndroidChannels(): Promise<void> {
  const push = capacitor()?.Plugins?.PushNotifications;
  if (!push) return;
  if (platformName() !== 'android' || !push.createChannel) return;
  try {
    await push.createChannel({
      id: 'maternally-crisis',
      name: 'Urgent support',
      description: 'Rare. Used when someone on your care team needs to reach you now.',
      importance: 5,
      visibility: 0,
      /* No `sound`, deliberately. The plugin turns whatever string it is given
         into android.resource://<pkg>/raw/<name>, so `sound: 'default'` asked
         for a raw file called "default" that does not exist — and a channel
         pointed at a missing sound is a silent one. Omitting it leaves the
         system notification sound. Caught on a handset: the one setting meant
         to make the crisis alert audible was what silenced it. */
      vibration: true,
    });
    await push.createChannel({
      id: 'maternally-general',
      name: 'Reminders and check-ins',
      description: 'Mood reminders, check-in nudges, and care plan updates.',
      importance: 3,
      visibility: 0,
      vibration: false,
    });
  } catch (err) {
    /* An older shell without createChannel, or a channel the user has since
       edited. Neither is worth failing registration over — messages still
       arrive, just in the fallback channel. */
    console.warn('[push] could not create notification channels', err);
  }
}

/**
 * Ask for permission, get a token, hand it to the server.
 *
 * Safe to call more than once — `register()` is idempotent on both platforms
 * and the endpoint is `ON CONFLICT DO NOTHING`, so a second sign-in on the same
 * handset re-confirms the same row rather than duplicating it.
 *
 * Call AFTER sign-in, never before. The token is stored against a user, so
 * asking a stranger for notification permission before she has an account
 * spends the one prompt iOS gives you on a screen with nothing to justify it.
 */
export async function registerForPush(): Promise<void> {
  if (!isNative()) return;                       // browser PWA: nothing to do

  const push = capacitor()?.Plugins?.PushNotifications;
  if (!push) {
    console.warn('[push] native shell without the PushNotifications plugin — skipping');
    return;
  }

  try {
    let perm = await push.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await push.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      /* Her choice, and a legitimate one — stigma is high in these markets and
         a lock screen is readable by family. Nothing is retried; she can turn
         it on in system settings if she changes her mind. */
      console.info('[push] permission not granted — push disabled on this device');
      return;
    }

    // Listeners before register(), or the token can arrive with nobody home.
    await attachListeners(push);
    await push.register();
  } catch (err) {
    console.warn('[push] registration failed; the app continues without push', err);
  }
}

/**
 * Withdraw this device on sign-out.
 *
 * Without it the row survives logout and the next crisis alert for that account
 * is delivered to a handset somebody else may now be holding — the same
 * lock-screen-is-readable-by-family problem, aimed at the wrong person
 * entirely. Best-effort: a failure here must never block signing out.
 */
export async function unregisterFromPush(): Promise<void> {
  const token = currentToken;
  currentToken = null;
  if (!isNative() || !token) return;

  try {
    await capacitor()?.Plugins?.PushNotifications?.removeAllListeners();
  } catch { /* the shell is going away anyway */ }

  try {
    await api.delete('/users/me/push-token', { data: { token } });
  } catch (err) {
    console.warn('[push] could not withdraw the token on logout', err);
  }
}
